// @ts-nocheck
import React from 'react'
import { Layout, Menu, Button, Avatar, Dropdown, Divider, Typography, Spin } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  MailOutlined,
  DashboardOutlined,
  SyncOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  HomeOutlined,
  TagOutlined,
  BellOutlined,
  TeamOutlined,
  CommentOutlined,
  StopOutlined,
  StarOutlined,
  FlagOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { useAuth } from '../../hooks/useAuth'
import { useLabels } from '../../hooks/useLabels'
import { getWebPortalUrl, getApiBaseUrl } from '../../config/runtimeEnv'
import type { MenuProps } from 'antd'

const { Text } = Typography

function resolveAvatarUrl(avatar: string | undefined): string | undefined {
  if (!avatar) return undefined
  if (avatar.startsWith('http://') || avatar.startsWith('https://')) return avatar
  const base = getApiBaseUrl().replace(/\/api\/?$/, '')
  return `${base}/uploads/${avatar}`
}

const { Header, Content, Sider } = Layout

interface AppLayoutProps {
  children: React.ReactNode
}

// Map label IDs to icons
const labelIconMap: Record<string, React.ReactNode> = {
  'STARRED': <StarOutlined style={{ color: '#faad14' }} />,
  'IMPORTANT': <FlagOutlined style={{ color: '#ff4d4f' }} />,
  'TRASH': <DeleteOutlined />,
  'SPAM': <StopOutlined style={{ color: '#ff4d4f' }} />,
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { emailAddress, profile } = useAuth()
  const { userLabels, isLoading: labelsLoading } = useLabels()

  const menuItems = [
    {
      key: '/inbox',
      icon: <MailOutlined />,
      label: 'Inbox',
    },
    {
      key: '/inbox/promotions',
      icon: <TagOutlined />,
      label: 'Promotions',
    },
    {
      key: '/inbox/updates',
      icon: <BellOutlined />,
      label: 'Updates',
    },
    {
      key: '/inbox/social',
      icon: <TeamOutlined />,
      label: 'Social',
    },
    {
      key: '/inbox/forums',
      icon: <CommentOutlined />,
      label: 'Forums',
    },
    {
      key: '/inbox/spam',
      icon: <StopOutlined />,
      label: 'Spam',
    },
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
  ]

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'email',
      label: emailAddress || 'Gmail Account',
      icon: <UserOutlined />,
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'main-portal',
      label: 'Main Portal',
      icon: <HomeOutlined />,
      onClick: () => {
        const webPortalUrl = getWebPortalUrl()
        window.location.href = webPortalUrl
      },
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: <SettingOutlined />,
      onClick: () => {
        const webPortalUrl = getWebPortalUrl()
        window.location.href = `${webPortalUrl}/settings/integrations`
      },
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: () => {
        localStorage.removeItem('token')
        const webPortalUrl = getWebPortalUrl()
        window.location.href = `${webPortalUrl}/login`
      },
    },
  ]

  const handleSync = () => {
    window.location.reload()
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        background: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '0 24px',
        zIndex: 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <MailOutlined style={{ fontSize: '24px', color: '#ea4335' }} />
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
            ServiceFlow Pro <span style={{ color: '#666' }}>| Gmail</span>
          </h1>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button
            type="text"
            icon={<SyncOutlined />}
            onClick={handleSync}
          >
            Sync
          </Button>
          
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar
              src={resolveAvatarUrl(profile?.avatar)}
              style={{ backgroundColor: profile?.avatar ? undefined : '#ea4335', cursor: 'pointer' }}
              icon={!profile?.avatar ? <UserOutlined /> : undefined}
            />
          </Dropdown>
        </div>
      </Header>

      <Layout>
        <Sider
          width={200}
          style={{
            background: '#fff',
            borderRight: '1px solid #f0f0f0',
            overflow: 'auto',
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname === '/' ? '/inbox' : location.pathname]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
            style={{ borderRight: 0 }}
          />
          
          {/* Labels Section */}
          <div style={{ padding: '16px 24px 8px' }}>
            <Divider style={{ margin: '8px 0' }} />
            <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 600 }}>
              Labels
            </Text>
          </div>
          
          {labelsLoading ? (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <Spin size="small" />
            </div>
          ) : userLabels.length > 0 ? (
            <Menu
              mode="inline"
              selectedKeys={[]}
              items={userLabels.map((label) => ({
                key: `label-${label.id}`,
                icon: labelIconMap[label.id] || <TagOutlined style={{ color: label.color?.backgroundColor }} />,
                label: label.name,
              }))}
              onClick={({ key }) => {
                const labelId = key.replace('label-', '')
                navigate(`/inbox?label=${encodeURIComponent(labelId)}`)
              }}
              style={{ borderRight: 0 }}
            />
          ) : (
            <div style={{ padding: '8px 24px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                No labels found
              </Text>
            </div>
          )}
        </Sider>

        <Layout style={{ padding: '24px' }}>
          <Content
            style={{
              padding: 24,
              margin: 0,
              minHeight: 280,
              background: '#fff',
              borderRadius: '8px',
            }}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  )
}

export default AppLayout

